import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Button from "../Button/Button";
import ExportFormatChoiceGrid from "../ExportFormatChoice/ExportFormatChoiceGrid";
import {
  EXPORT_FORMAT_ORDER,
  getExportFormatUnavailableReason,
} from "../ExportFormatChoice/exportFormatDisplay";
import Popup from "../Popup/Popup";
import { EXPORT_POPUP_SHELL_STYLE } from "../../../Utils/Export/exportPopupShellStyle";
import type { ExportFormat } from "../../../Utils/Export/types";
import { ImproveTemplateDto } from "../../../Types/Improve/ImproveTemplateDto";
import styles from "./ExportButton.module.css";
import wizardStyles from "../LandingPages/BulkExportWizard/BulkExportWizard.module.css";
import { downloadBlob } from "../../../Utils/Export/downloadBlob";
import { openBlobInNewTab } from "../../../Utils/Export/openInNewTab";
import { fetchImproveExportData } from "../../../Utils/Export/ImproveExport/fetchImproveExportData";
import { improveDocxExporter } from "../../../Utils/Export/ImproveExport/improveDocxExporter";
import { improveHtmlExporter } from "../../../Utils/Export/ImproveExport/improveHtmlExporter";
import { improveXlsxExporter } from "../../../Utils/Export/ImproveExport/improveXlsxExporter";
import {
  DEFAULT_IMPROVE_EXPORT_FIELD_VISIBILITY,
  IMPROVE_EXPORT_COLS,
  ImproveExportColumnKey,
  ImproveExportFieldVisibility,
  fullImproveExportFieldVisibility,
} from "../../../Utils/Export/ImproveExport/types";
import {
  buildInitialImproveExportFieldVisibility,
  clampImproveExportFieldVisibility,
  getImproveExportColumnToggleableMap,
  isImproveExportColumnToggleable,
} from "../../../Utils/Export/ImproveExport/improveExportTemplateFields";
import ImproveExportColumnPicker from "./ImproveExportColumnPicker";

const IMPROVE_EXPORT_FORMATS: ExportFormat[] = EXPORT_FORMAT_ORDER;

function defaultImproveBaseFilename(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `Mesma-Track-Export-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}`;
}

export type ImproveExportButtonHandle = {
  trigger: () => void;
};

export type ImproveExportButtonProps = {
  areaId: string;
  label?: string;
  disabled?: boolean;
  size?: "Small" | "Medium" | null;
  className?: string;
  /** Plan template — controls which export columns can be toggled on. */
  template?: ImproveTemplateDto;
  /** When true, matches archive-tab payloads (ImproveExportWizard). */
  archivedPlans?: boolean;
  onDone?: () => void;
  /** When true, only the wizard popup is rendered (no visible button). */
  hideTrigger?: boolean;
  /** Flat styling for use inside a grouped toolbar (no individual button chrome). */
  embedded?: boolean;
};

const ImproveExportButton = forwardRef<ImproveExportButtonHandle, ImproveExportButtonProps>(
  function ImproveExportButton(
    {
      areaId,
      label = "Export",
      disabled = false,
      size = "Small",
      className,
      template,
      archivedPlans = false,
      onDone,
      hideTrigger = false,
      embedded = false,
    },
    ref
  ) {
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState<2 | 3>(2);
  const [fieldVisibility, setFieldVisibility] =
    useState<ImproveExportFieldVisibility>(() => ({
      ...DEFAULT_IMPROVE_EXPORT_FIELD_VISIBILITY,
    }));
  const [error, setError] = useState<string | null>(null);

  const templates = useMemo(
    () => (template ? [template] : []),
    [template]
  );

  const resetFieldVisibility = useCallback(() => {
    setFieldVisibility(
      template
        ? buildInitialImproveExportFieldVisibility([template])
        : { ...DEFAULT_IMPROVE_EXPORT_FIELD_VISIBILITY }
    );
  }, [template]);

  const activeColumnCount = useMemo(
    () => IMPROVE_EXPORT_COLS.filter((k) => fieldVisibility[k]).length,
    [fieldVisibility]
  );

  const closeWizard = useCallback(() => {
    setWizardOpen(false);
    setStep(2);
    resetFieldVisibility();
    setError(null);
    setLoading(false);
  }, [resetFieldVisibility]);

  useEffect(() => {
    if (!template) return;
    setFieldVisibility((prev) =>
      clampImproveExportFieldVisibility(prev, [template])
    );
  }, [template]);

  const setColumnOn = useCallback(
    (key: ImproveExportColumnKey, on: boolean) => {
      if (!isImproveExportColumnToggleable(key, templates)) return;
      setFieldVisibility((prev) => ({ ...prev, [key]: on }));
    },
    [templates]
  );

  const toggleableMap = useMemo(
    () => getImproveExportColumnToggleableMap(templates),
    [templates]
  );

  const allColumnsSelected = useMemo(() => {
    const keys = IMPROVE_EXPORT_COLS.filter((k) => toggleableMap[k]);
    return keys.length > 0 && keys.every((k) => fieldVisibility[k]);
  }, [fieldVisibility, toggleableMap]);

  const toggleSelectAllColumns = useCallback(() => {
    setFieldVisibility((prev) => {
      const toggleableKeys = IMPROVE_EXPORT_COLS.filter((k) => toggleableMap[k]);
      const allOn =
        toggleableKeys.length > 0 && toggleableKeys.every((k) => prev[k]);
      const next = { ...prev };
      for (const k of IMPROVE_EXPORT_COLS) {
        if (toggleableMap[k]) next[k] = !allOn;
        else next[k] = false;
      }
      return next;
    });
  }, [toggleableMap]);

  const runExport = useCallback(
    async (format: ExportFormat) => {
      if (getExportFormatUnavailableReason(format, true)) {
        return;
      }

      if (!areaId) {
        toast.error("No plan to export.");
        return;
      }
      if (activeColumnCount === 0) {
        setError("Select at least one field to include in the export.");
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const vis = fullImproveExportFieldVisibility(fieldVisibility);
        const data = await fetchImproveExportData([areaId], {
          archived: archivedPlans,
        });
        if (data.length === 0) {
          toast.error("No data returned for export. Check your permissions.");
          return;
        }
        const base = defaultImproveBaseFilename();
        const exporter =
          format === "xlsx"
            ? improveXlsxExporter
            : format === "docx"
              ? improveDocxExporter
              : improveHtmlExporter;
        const ext =
          format === "xlsx" ? "xlsx" : format === "docx" ? "docx" : "html";
        const blob = await exporter(data, base, { fieldVisibility: vis });
        if (format === "html") {
          openBlobInNewTab(blob);
        } else {
          downloadBlob(blob, `${base}.${ext}`);
        }
        toast.success("Export completed.");
        onDone?.();
        closeWizard();
      } catch (e) {
        console.error(e);
        toast.error("Export failed. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [activeColumnCount, areaId, archivedPlans, closeWizard, fieldVisibility, onDone]
  );

  const handleClick = useCallback(() => {
    if (loading || disabled) return;
    resetFieldVisibility();
    setWizardOpen(true);
  }, [disabled, loading, resetFieldVisibility]);

  useImperativeHandle(ref, () => ({ trigger: handleClick }), [handleClick]);

  const border = disabled ? "#e0e0e0" : embedded ? "transparent" : "rgba(0,0,0,0.2)";
  const canNextStep2 = activeColumnCount > 0;

  return (
    <>
      {!hideTrigger && (
        <Button
          className={`${styles.wrap} ${embedded ? styles.embedded : ""} ${className ?? ""}`}
          size={size === "Medium" ? null : size}
          colour={embedded ? "transparent" : "#FFF"}
          textColour="#000"
          border={border}
          disabled={disabled || loading}
          suppressHover={embedded}
          clickHandler={handleClick}
        >
          <span className={styles.inner}>
            {loading ? <span className={styles.spinner} aria-hidden /> : null}
            <span>{loading ? "Exporting…" : label}</span>
            {embedded && !loading ? (
              <img
                src="/img/document-upload.svg"
                alt=""
                className={styles.exportIcon}
                aria-hidden
              />
            ) : null}
          </span>
        </Button>
      )}

      <Popup
        isVisible={wizardOpen}
        onClose={closeWizard}
        style={EXPORT_POPUP_SHELL_STYLE}
      >
        <div className={wizardStyles.content}>
          <h3 className={wizardStyles.contentTitle}>Export track plan</h3>

          <div className={wizardStyles.stepMain}>
            {step === 2 ? (
              <ImproveExportColumnPicker
                fieldVisibility={fieldVisibility}
                templates={templates}
                busy={loading}
                onColumnChange={setColumnOn}
                onToggleSelectAll={toggleSelectAllColumns}
                allColumnsSelected={allColumnsSelected}
              />
            ) : null}

            {step === 3 ? (
              <>
                <p className={wizardStyles.stepHint}>
                  Choose a file format. Your column choices apply to Excel, Word,
                  and HTML.
                </p>
                <div className={wizardStyles.formatRow}>
                  <ExportFormatChoiceGrid
                    variant="tiles"
                    formats={IMPROVE_EXPORT_FORMATS}
                    trackExport
                    disabled={loading}
                    onChoose={(fmt) => void runExport(fmt)}
                  />
                  {loading ? (
                    <p className={wizardStyles.formatBusy} aria-live="polite">
                      Working on your export…
                    </p>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>

          {error ? <p className={wizardStyles.error}>{error}</p> : null}

          <div className={wizardStyles.actions}>
            {step === 3 ? (
              <Button
                size="Small"
                colour="#FFF"
                textColour="#000"
                border="rgba(0,0,0,0.2)"
                disabled={loading}
                clickHandler={() => setStep(2)}
              >
                Back
              </Button>
            ) : null}
            {step === 2 ? (
              <Button
                size="Small"
                colour="rgb(36, 57, 79)"
                textColour="#FFF"
                border="transparent"
                disabled={loading || !canNextStep2}
                clickHandler={() => {
                  setError(null);
                  setStep(3);
                }}
              >
                Next
              </Button>
            ) : null}
            <Button
              size="Small"
              colour="#FFF"
              textColour="#000"
              border="rgba(0,0,0,0.2)"
              disabled={loading}
              clickHandler={closeWizard}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Popup>
    </>
  );
});

export default ImproveExportButton;
